// frontend/src/utils/pageTeardown.js
//
// "Is this document on its way out?" — one answer, for the guards that must
// not read a request killed by the user leaving as a verdict from the server.
//
// A fetch aborted at unload rejects exactly like a network error, so a guard
// that fails closed on rejection paints its failure state over the last frames
// of a page the user has already left. Suppressing that is not a swallowed
// error and not a substituted value: the request never reached a verdict, and
// "the server said no" is a different fact from "nobody was left to ask".
// Everything else about failing closed is unchanged — a rejection while the
// document is staying put still blocks.
//
// `pagehide` fires when the document is unloaded OR frozen into the back/
// forward cache; `pageshow` fires both on an ordinary load and on a restore
// from that cache, with `persisted` telling the two apart
// (developer.mozilla.org/en-US/docs/Web/API/Window/pagehide_event and
// .../pageshow_event). A restored document keeps its JS heap,
// so a check that declined to record its outcome would otherwise sit
// unfinished forever behind a loading screen — hence `onPageRestored`, which
// the guard uses to re-run it. The flag is cleared before subscribers run, so
// a re-armed request can never see a stale "we are leaving".
//
// MDN also documents that `pagehide` is NOT reliably fired (notably on
// mobile), and that is what makes this safe to build on: a missed `pagehide`
// leaves the flag false, so the guard records the failure and denies access.
// The failure direction is "gate the merchant", never "let them through".

let leaving = false;
const restoreSubscribers = new Set();

window.addEventListener('pagehide', () => { leaving = true; });
window.addEventListener('pageshow', (event) => {
  leaving = false;
  // A fresh load has nothing to re-arm — that tree is new and runs its checks
  // from the top. Only a bfcache restore brings back state that stopped
  // mid-flight.
  if (event.persisted) restoreSubscribers.forEach((callback) => callback());
});

// True only between the document being torn down / frozen and a restore.
export function isPageBeingReplaced() {
  return leaving;
}

// Subscribe to "this document came back after all" (bfcache restore).
// Returns the unsubscribe function, for an effect's cleanup.
export function onPageRestored(callback) {
  restoreSubscribers.add(callback);
  return () => restoreSubscribers.delete(callback);
}
